
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function MilnertonPage() {
  useEffect(() => {
    document.title = 'Milnerton Painters Cape Town | Residential Commercial Painting | Table Bay Area';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Milnerton, Cape Town. Expert residential and commercial painting for Table Bay area properties. Quality workmanship and competitive prices.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Milnerton painters, Table Bay painting, residential commercial Cape Town');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/milnerton`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Milnerton Painters Cape Town",
      "description": "Professional painting services in Milnerton, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/milnerton`,
      "areaServed": {
        "@type": "Place",
        "name": "Milnerton, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.8769,
          "longitude": 18.4896
        },
        "geoRadius": "12000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Milnerton Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c6e77ecb14da058e7e7a048b89e97bc1.jpg"
            alt="Professional painting services in Milnerton, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Trusted <span className="text-yellow-400">Milnerton Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for Milnerton's diverse residential and commercial properties. Quality craftsmanship and reliable service in the Table Bay area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Complete Painting Services in Milnerton
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From family homes to commercial developments, we provide comprehensive painting solutions for all property types in Milnerton and the greater Table Bay area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Residential Painting',
                description: 'Complete home painting services for Milnerton families, from interior rooms to exterior facades and garden walls.',
                features: ['Interior painting', 'Exterior painting', 'Roof painting', 'Garden walls']
              },
              {
                icon: 'ri-building-line',
                title: 'Commercial Properties',
                description: 'Professional commercial painting for offices, retail spaces, and industrial facilities in Milnerton business areas.',
                features: ['Office buildings', 'Retail centers', 'Industrial facilities', 'Shopping complexes']
              },
              {
                icon: 'ri-community-line',
                title: 'Residential Complexes',
                description: 'Specialized painting services for apartment complexes, townhouse developments, and gated communities.',
                features: ['Apartment complexes', 'Townhouse developments', 'Gated communities', 'Common areas']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                      <i className={`${feature.icon} text-black text-2xl`}></i>
                    </div>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Milnerton Residents Choose Our Painting Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Local Milnerton Knowledge',
                    description: 'Deep understanding of Milnerton\'s diverse property types, from established neighborhoods to new developments.'
                  },
                  {
                    title: 'Quality Materials',
                    description: 'Premium paints and materials suitable for Table Bay area conditions, ensuring long-lasting, beautiful results.'
                  },
                  {
                    title: 'Flexible Scheduling',
                    description: 'Convenient scheduling options to accommodate busy family and business schedules in the Milnerton community.'
                  },
                  {
                    title: 'Competitive Pricing',
                    description: 'Fair, transparent pricing with detailed quotes and excellent value for money on all Milnerton painting projects.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Milnerton%20residential%20property%2C%20Table%20Bay%20area%20home%20painting%2C%20skilled%20painting%20team%20with%20quality%20equipment%2C%20suburban%20Cape%20Town%20painting%20services&width=600&height=700&seq=milnerton-team-work&orientation=portrait"
                alt="Professional painting team in Milnerton"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Milnerton Painting Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our latest completed projects in Milnerton, showcasing our versatility and commitment to quality across different property types.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Modern Family Home',
                location: 'Milnerton Ridge',
                description: 'Complete interior and exterior painting of a contemporary 4-bedroom family home.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20painting%20in%20Milnerton%20Ridge%20Cape%20Town%2C%20contemporary%20residential%20architecture%2C%20professional%20exterior%20and%20interior%20painting%2C%20suburban%20family%20property&width=400&height=300&seq=milnerton-project-1&orientation=landscape'
              },
              {
                title: 'Shopping Center Renovation',
                location: 'Milnerton Central',
                description: 'Comprehensive painting renovation for a busy neighborhood shopping center.',
                image: 'https://readdy.ai/api/search-image?query=Shopping%20center%20painting%20renovation%20in%20Milnerton%2C%20commercial%20retail%20property%20painting%2C%20modern%20shopping%20complex%2C%20professional%20commercial%20painting%20Cape%20Town&width=400&height=300&seq=milnerton-project-2&orientation=landscape'
              },
              {
                title: 'Townhouse Complex',
                location: 'Milnerton Golf Estate',
                description: 'Exterior painting project for a 24-unit townhouse development with modern finishes.',
                image: 'https://readdy.ai/api/search-image?query=Townhouse%20complex%20painting%20in%20Milnerton%20Golf%20Estate%2C%20residential%20development%20painting%2C%20modern%20townhouse%20architecture%2C%20professional%20complex%20painting%20Cape%20Town&width=400&height=300&seq=milnerton-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Milnerton Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Read testimonials from satisfied customers across Milnerton and the Table Bay area
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Mark Johnson',
                location: 'Milnerton Ridge',
                rating: 5,
                text: 'Excellent work on our family home. The team was professional, punctual, and delivered exactly what they promised. Our house looks fantastic!'
              },
              {
                name: 'Lisa Patel',
                location: 'Milnerton Central',
                rating: 5,
                text: 'Outstanding commercial painting service for our retail space. Minimal disruption to business and exceptional results. Highly recommended for commercial properties.'
              },
              {
                name: 'Andrew Smith',
                location: 'Milnerton Golf Estate',
                rating: 5,
                text: 'Professional service from start to finish. Great communication, quality workmanship, and fair pricing. Will definitely use them again for future projects.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Transform Your Milnerton Property Today
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a free consultation and detailed quote. Experience the difference of professional painting services tailored to Milnerton properties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
